import { createFileRoute, useMatches } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { AdminShell } from '@/admin/AdminShell';
import { canAccessAdmin, hasTier, routeMinTier } from '@/admin/access';
import { errorCode } from '@/api/errors';
import { ErrorScreen, NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * Gate and shell for every `/admin/*` screen. A signed-in non-admin gets a 404
 * rather than a redirect, so the console's existence isn't advertised. Defence in depth: the server's
 * `current_admin` dependency is the real gate.
 */
function AdminLayout(): ReactElement | null {
  const { me, isPending, isError, failureReason, refetch } = useCurrentUser();
  // The deepest match is the screen being opened; an `/admin/*` route that
  // declares nothing is ADMIN-only.
  const matches = useMatches();
  const deepest = matches.at(-1);

  // `/users/me` is pending on first paint; a bare `!allowed` would 404 the
  // admin for that frame.
  if (isPending) return null;
  // "Could not tell who you are" is not "not an admin": only an invalid token
  // (or a resolved non-admin) is a 404; anything else gets a retry. With a
  // cached profile the failure is a background refetch, and `me` still decides.
  if (isError && !me && errorCode(failureReason) !== 'invalid_auth_token') {
    return <ErrorScreen error={failureReason} onRetry={() => refetch()} />;
  }
  if (!canAccessAdmin(me)) return <NotFoundScreen />;
  if (!hasTier(me, routeMinTier(deepest?.staticData))) {
    return <NotFoundScreen />;
  }

  return <AdminShell />;
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});
